import { Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

const SocialIcons = () => {
  const socials = [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/UAEGlowSpace/',
      color: '#1877F2',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/glow.space1?igsh=cnNhZ3JqYjVlcTl0&utm_source=qr',
      gradient: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/glow-space-contracting/',
      color: '#0A66C2',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="flex items-center gap-3">
      {socials.map((social) => (
        <a
          key={social.name}
          href={social.href}
          target="_blank"
          rel="noopener noreferrer"
          className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
          style={{ 
            background: social.gradient || social.color,
            color: 'white'
          }}
          data-testid={`social-${social.name.toLowerCase()}`}
          aria-label={`Follow us on ${social.name}`}
        >
          {social.icon}
        </a>
      ))}
    </div>
  );
};

const Footer = () => {
  const { language, isRTL } = useLanguage();
  const currentYear = new Date().getFullYear();

  const content = {
    en: {
      tagline: 'Aluminium & Glass',
      description: 'Premium aluminium and glass solutions in the UAE. Original Canadian quality at competitive prices.',
      ourServices: 'Our Services',
      quickLinks: 'Quick Links',
      getQuote: 'Get a Free Quote',
      quoteDesc: 'Ready to start your project? Contact us on WhatsApp for a quick response and free consultation.',
      chatWhatsApp: 'Chat on WhatsApp',
      copyright: `© ${currentYear} Glow Space LLC. All rights reserved.`,
      services: [
        'Windows & Doors',
        'Glass Partitions',
        'Shower Enclosures',
        'Glass Railings',
        'Curtain Walls',
        'Skylights',
      ],
      links: [
        { name: 'Services', href: '#services' },
        { name: 'Portfolio', href: '#portfolio' },
        { name: 'Projects', href: '#projects' },
        { name: 'About Us', href: '#about' },
        { name: 'Blog', href: '/blog', isRoute: true },
        { name: 'Contact', href: '#contact' },
      ],
      seoTags: ['Aluminium Works UAE', 'Glass Contractors Dubai', 'Maintenance Services UAE'],
    },
    ar: {
      tagline: 'ألمنيوم وزجاج',
      description: 'حلول الألمنيوم والزجاج الفاخرة في الإمارات. جودة كندية أصلية بأسعار تنافسية.',
      ourServices: 'خدماتنا',
      quickLinks: 'روابط سريعة',
      getQuote: 'احصل على عرض سعر مجاني',
      quoteDesc: 'مستعد لبدء مشروعك؟ تواصل معنا عبر واتساب للحصول على رد سريع واستشارة مجانية.',
      chatWhatsApp: 'تحدث عبر واتساب',
      copyright: `© ${currentYear} جلو سبيس ذ.م.م. جميع الحقوق محفوظة.`,
      services: [
        'نوافذ وأبواب',
        'فواصل زجاجية',
        'كبائن الاستحمام',
        'درابزين زجاجي',
        'الستائر الزجاجية',
        'فتحات السقف',
      ],
      links: [
        { name: 'الخدمات', href: '#services' },
        { name: 'أعمالنا', href: '#portfolio' },
        { name: 'المشاريع', href: '#projects' },
        { name: 'من نحن', href: '#about' },
        { name: 'المدونة', href: '/blog', isRoute: true },
        { name: 'اتصل بنا', href: '#contact' },
      ],
      seoTags: ['أعمال الألمنيوم الإمارات', 'مقاولي الزجاج دبي', 'خدمات الصيانة الإمارات'],
    },
  };

  const t = content[language];
  const fontFamily = isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif";

  return (
    <footer 
      className="bg-sky-900 border-t border-sky-800"
      data-testid="footer"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className={isRTL ? 'text-right' : ''}>
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div 
                className="h-20 w-20 flex-shrink-0"
                style={{
                  backgroundImage: 'url(https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/67qwmrqp_imgs_glow_space_logo_transparent.png)',
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  filter: 'brightness(1.3)'
                }}
              />
              <div className={isRTL ? 'text-right' : ''}>
                <span className="text-xl font-semibold tracking-tight text-white" style={{ fontFamily }}>
                  {isRTL ? 'جلو سبيس' : 'GLOW SPACE'}
                </span>
                <span className="block text-[10px] tracking-[0.3em] text-sky-300 uppercase" style={{ fontFamily }}>
                  {t.tagline}
                </span>
              </div>
            </div>
            <p className="text-sky-200 text-sm leading-relaxed mb-6" style={{ fontFamily }}>
              {t.description}
            </p>
            <div className="space-y-3">
              <a 
                href="tel:+971566832840"
                className={`flex items-center gap-3 text-sky-200 hover:text-white transition-colors text-sm ${isRTL ? 'flex-row-reverse' : ''}`}
                data-testid="footer-phone"
              >
                <Phone size={16} />
                <span dir="ltr">+971 56 683 2840</span>
              </a>
              <a 
                href="mailto:info@glow-space.com"
                className={`flex items-center gap-3 text-sky-200 hover:text-white transition-colors text-sm ${isRTL ? 'flex-row-reverse' : ''}`}
                data-testid="footer-email"
              >
                <Mail size={16} />
                <span dir="ltr">info@glow-space.com</span>
              </a>
              {/* Head Office - Dubai */}
              <div className={`flex items-start gap-3 text-sky-200 text-sm ${isRTL ? 'flex-row-reverse' : ''}`} style={{ fontFamily }}>
                <MapPin size={16} className="flex-shrink-0 mt-0.5 text-[#25D366]" />
                <div>
                  <span className="text-sky-100 font-medium block text-xs mb-0.5">{isRTL ? 'المكتب الرئيسي' : 'Head Office'}</span>
                  {isRTL ? 'برج تشامبيون، مدينة دبي الرياضية، دبي' : 'Champion Tower, Dubai Sports City, Dubai'}
                </div>
              </div>
              {/* Showroom - Al Quoz */}
              <div className={`flex items-start gap-3 text-sky-200 text-sm ${isRTL ? 'flex-row-reverse' : ''}`} style={{ fontFamily }}>
                <MapPin size={16} className="flex-shrink-0 mt-0.5 text-[#25D366]" />
                <div>
                  <span className="text-sky-100 font-medium block text-xs mb-0.5">{isRTL ? 'صالة العرض' : 'Showroom'}</span>
                  {isRTL ? 'القوز، دبي، الإمارات' : 'Al Quoz, Dubai, UAE'}
                </div>
              </div>
              {/* Al Ain Office */}
              <div className={`flex items-start gap-3 text-sky-200 text-sm ${isRTL ? 'flex-row-reverse' : ''}`} style={{ fontFamily }}>
                <MapPin size={16} className="flex-shrink-0 mt-0.5 text-sky-400" />
                <div>
                  <span className="text-sky-100 font-medium block text-xs mb-0.5">{isRTL ? 'مكتب العين' : 'Al Ain Office'}</span>
                  {isRTL ? 'الأشرج، العين، الإمارات' : 'Asharej, Al Ain, UAE'}
                </div>
              </div>
              {/* Workshop - Sharjah */}
              <div className={`flex items-start gap-3 text-sky-200 text-sm ${isRTL ? 'flex-row-reverse' : ''}`} style={{ fontFamily }}>
                <MapPin size={16} className="flex-shrink-0 mt-0.5 text-sky-400" />
                <div>
                  <span className="text-sky-100 font-medium block text-xs mb-0.5">{isRTL ? 'ورشة العمل' : 'Workshop'}</span>
                  {isRTL ? 'مليحة، الشارقة، الإمارات' : 'Maliha, Sharjah, UAE'}
                </div>
              </div>
            </div>
            
            {/* Social Media Icons */}
            <div className={`mt-6 ${isRTL ? 'flex justify-end' : ''}`}>
              <SocialIcons />
            </div>
          </div>

          {/* Services */}
          <div className={isRTL ? 'text-right' : ''}>
            <h4 
              className="text-white font-medium mb-6"
              style={{ fontFamily }}
            >
              {t.ourServices}
            </h4>
            <ul className="space-y-3">
              {t.services.map((service, index) => (
                <li key={index}>
                  <a 
                    href="#services"
                    className="text-sky-200 hover:text-white transition-colors text-sm"
                    style={{ fontFamily }}
                    data-testid={`footer-service-${index}`}
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div className={isRTL ? 'text-right' : ''}>
            <h4 
              className="text-white font-medium mb-6"
              style={{ fontFamily }}
            >
              {t.quickLinks}
            </h4>
            <ul className="space-y-3">
              {t.links.map((link, index) => (
                <li key={index}>
                  {link.isRoute ? (
                    <Link 
                      to={link.href}
                      className="text-sky-200 hover:text-white transition-colors text-sm"
                      style={{ fontFamily }}
                      data-testid={`footer-link-${index}`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a 
                      href={link.href}
                      className="text-sky-200 hover:text-white transition-colors text-sm"
                      style={{ fontFamily }}
                      data-testid={`footer-link-${index}`}
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className={isRTL ? 'text-right' : ''}>
            <h4 
              className="text-white font-medium mb-6"
              style={{ fontFamily }}
            >
              {t.getQuote}
            </h4>
            <p className="text-sky-200 text-sm leading-relaxed mb-6" style={{ fontFamily }}>
              {t.quoteDesc}
            </p>
            <a
              href="https://wa.me/971566832840?text=Hello%2C%20I%27m%20interested%20in%20your%20services"
              target="_blank"
              rel="noopener noreferrer"
              className={`btn-whatsapp text-sm w-full ${isRTL ? 'flex-row-reverse' : ''}`}
              style={{ fontFamily }}
              data-testid="footer-whatsapp-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t.chatWhatsApp}
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-16 pt-8 border-t border-sky-800 flex flex-col md:flex-row justify-between items-center gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
          <p className="text-sky-300 text-sm" style={{ fontFamily }}>
            {t.copyright}
          </p>
          <div className={`flex items-center gap-4 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link 
              to="/privacy-policy" 
              className="text-sky-300 hover:text-white transition-colors"
              style={{ fontFamily }}
              data-testid="footer-privacy-policy"
            >
              {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
            </Link>
            <span className="text-sky-700">|</span>
            <Link 
              to="/terms-of-service" 
              className="text-sky-300 hover:text-white transition-colors"
              style={{ fontFamily }}
              data-testid="footer-terms-of-service"
            >
              {language === 'ar' ? 'شروط الخدمة' : 'Terms of Service'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
